import React, { useState, useEffect, useMemo } from 'react';
import { useLiveMatchWebhook } from '../context/LiveMatchWebhookContext';
import LiveTeamStatsSection from './match/LiveTeamStatsSection';
import RoundsVisualTimeline from './match/RoundsVisualTimeline';
import VetoSection from './match/VetoSection';
import MatchHero from './match/MatchHero';
import { parseVetoLog } from '../utils/vetoParser';
import { useMappoolContext } from '../context/MappoolContext';
import '../styles/components/live-match-view.scss';
import { Match, Team, getTeamLogoUrl, formatDateTime } from '../pages/MatchPage';
import { FaTwitch, FaCheckCircle, FaCircle, FaClock } from 'react-icons/fa';
import { matchService } from '../services/matchService';

interface Round {
  round: number;
  winner: string;
  reason: string;
}

interface LivePlayerStats {
  steam_id: string;
  player_name: string;
  kills: number;
  deaths: number;
  assists: number;
  adr: number;
  kast: number;
  headshot_kills: number;
  mvp: number;
  rating: number;
}

interface TeamInfo {
  teamId: string | number;  // Database team ID (for routing) - can be UUID or number
  webhookTeamId: number;    // Get5 webhook team ID (for filtering stats)
  teamName: string;
  teamLogo: string;
  score: number;
  side: string;
  seriesScore: number;
}

interface LiveMatchViewProps {
  match?: Match;
  onMapStateChange?: (mapState: {
    mapName: string;
    mapNumber?: number;
    team1SeriesScore?: number;
    team2SeriesScore?: number;
    team1Score?: number;
    team2Score?: number;
    isLiveMap?: boolean;
    liveMapNumber?: number;
    currentRound?: number;
    maxRounds?: number;
  }) => void;
  showHero?: boolean; // Control whether to show the MatchHero header
}

interface MapInfo {
  map_number: number;
  map_name: string;
  get5_match_id: number;
  status: 'created' | 'live' | 'finished';
  created_at: string;
  updated_at: string;
}

interface MapStatusResponse {
  matchId: string;
  maps: MapInfo[];
  totalMaps: number;
  completedMaps: number;
  liveMaps: number;
  pendingMaps: number;
}

const LiveMatchView: React.FC<LiveMatchViewProps> = ({ match, onMapStateChange, showHero = true }) => {
  const { liveState: sseLiveState, roundHistory: sseRoundHistory, playerStats: ssePlayerStats, isLoading, isLive, isPostMatch, hasDetailedStats } = useLiveMatchWebhook();
  const { getMappool } = useMappoolContext();

  // Stream state
  const [showStream, setShowStream] = useState<boolean>(false); // Start collapsed by default
  const [isTwitchLoaded, setIsTwitchLoaded] = useState<boolean>(false);

  // Mappool state
  const [currentSeasonMaps, setCurrentSeasonMaps] = useState<string[]>([]);

  // Map selector state for BO3
  const [mapStatus, setMapStatus] = useState<MapStatusResponse | null>(null);
  const [selectedMapNumber, setSelectedMapNumber] = useState<number | null>(null);
  const [historicalData, setHistoricalData] = useState<{
    roundHistory: any[];
    playerStats: any[];
    liveState: any;
  } | null>(null);
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('[LiveMatchView] Component state:', {
      liveState: sseLiveState,
      roundHistory: sseRoundHistory,
      playerStats: ssePlayerStats,
      isLoading,
      isLive,
      isPostMatch
    });
  }, [sseLiveState, sseRoundHistory, ssePlayerStats, isLoading, isLive, isPostMatch]);

  // Debug player stats updates
  useEffect(() => {
    if (ssePlayerStats && ssePlayerStats.length > 0) {
      console.log('[LiveMatchView] Player stats updated:', {
        totalPlayers: ssePlayerStats.length,
        uniqueTeams: Array.from(new Set(ssePlayerStats.map(p => p.team_id))),
        allPlayers: ssePlayerStats.map(p => ({
          name: p.player_name,
          team_id: p.team_id,
          kills: p.kills,
          deaths: p.deaths,
          steamId: p.steam_id
        }))
      });
    }
  }, [ssePlayerStats]);

  // Debug match object
  useEffect(() => {
    console.log('[LiveMatchView] Match object debug:', {
      hasMatch: !!match,
      team1Id: match?.team1?.teamId,
      team1IdType: typeof match?.team1?.teamId,
      team2Id: match?.team2?.teamId,
      team2IdType: typeof match?.team2?.teamId,
      team1Full: match?.team1,
      team2Full: match?.team2
    });
  }, [match]);

  // Fetch map status for BO3 map selector
  useEffect(() => {
    const fetchMapStatus = async () => {
      if (!match?.challonge_id) return;

      try {
        const response = await matchService.getMapStatus(match.challonge_id);
        console.log('[LiveMatchView] Map status fetched:', response);
        setMapStatus(response);

        // Auto-select the live map or most recent map
        if (response.maps.length > 0) {
          const liveMap = response.maps.find((m: MapInfo) => m.status === 'live');
          const mostRecentMap = response.maps[response.maps.length - 1];
          setSelectedMapNumber(liveMap?.map_number || mostRecentMap.map_number);
        }
      } catch (error) {
        console.error('[LiveMatchView] Error fetching map status:', error);
      }
    };

    fetchMapStatus();
    // Refetch every 30 seconds to catch status changes
    const interval = setInterval(fetchMapStatus, 30000);
    return () => clearInterval(interval);
  }, [match?.challonge_id]);

  // Fetch historical data for finished maps
  useEffect(() => {
    const fetchHistoricalMapData = async () => {
      if (!match?.challonge_id || !selectedMapNumber || !mapStatus) return;

      const selectedMap = mapStatus.maps.find(m => m.map_number === selectedMapNumber);

      // Only fetch historical data for finished maps, use live SSE for live/created maps
      if (!selectedMap || selectedMap.status !== 'finished') {
        setHistoricalData(null);
        return;
      }

      setIsLoadingHistorical(true);

      const get5MatchId = selectedMap.get5_match_id;
      console.log(`[LiveMatchView] Fetching data for map ${selectedMapNumber} (${selectedMap.map_name}) with get5_match_id=${get5MatchId}`);

      try {
        // Fetch round history for the specific get5_match_id
        const roundHistoryResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/match/${match.challonge_id}/round-history?get5MatchId=${get5MatchId}`
        );
        const roundHistoryData = await roundHistoryResponse.json();

        // Fetch player stats for the specific get5_match_id
        const playerStatsResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/match/${match.challonge_id}/player-stats?get5MatchId=${get5MatchId}`
        );
        const playerStatsData = await playerStatsResponse.json();

        // Fetch live state for the specific map (contains final scores)
        const liveStateResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/match/${match.challonge_id}/live-state?get5MatchId=${get5MatchId}`
        );
        const liveStateData = await liveStateResponse.json();

        setHistoricalData({
          roundHistory: roundHistoryData || [],
          playerStats: playerStatsData?.players || [],
          liveState: liveStateData
        });

        console.log('[LiveMatchView] Historical data loaded:', {
          rounds: roundHistoryData?.length,
          players: playerStatsData?.players?.length
        });
      } catch (error) {
        console.error('[LiveMatchView] Error fetching historical data:', error);
        setHistoricalData(null);
      } finally {
        setIsLoadingHistorical(false);
      }
    };

    fetchHistoricalMapData();
  }, [match?.challonge_id, selectedMapNumber, mapStatus]);

  // Twitch embed effect
  useEffect(() => {
    // Only proceed if we have a stream URL and want to show the stream
    if (!match?.stream_url || !showStream) {
      return;
    }

    let channelName = "";
    let mountedRef = true;

    try {
      // Extract channel name from Twitch URL
      const url = new URL(match.stream_url);
      if (url.hostname === "www.twitch.tv" || url.hostname === "twitch.tv") {
        channelName = url.pathname.replace("/", "");
      }
    } catch (error) {
      console.error("Error parsing Twitch URL:", error);
      return;
    }

    if (!channelName) {
      // No valid channel name found
      const container = document.getElementById("twitch-embed-live");
      if (container) {
        container.innerHTML = `
          <div class="twitch-fallback">
            <p>Unable to embed stream directly.</p>
            <a href="${match.stream_url}" target="_blank" rel="noopener noreferrer" class="stream-link">
              <span>Watch on Twitch</span>
            </a>
          </div>
        `;
      }
      return;
    }

    // Clean up any existing Twitch scripts to avoid duplicates
    const existingScripts = document.querySelectorAll(
      'script[src="https://embed.twitch.tv/embed/v1.js"]'
    );
    existingScripts.forEach((script) => script.remove());

    // Create and load the Twitch embed script
    const script = document.createElement("script");
    script.src = "https://embed.twitch.tv/embed/v1.js";
    script.async = true;

    script.onload = () => {
      if (!mountedRef) return;

      setTimeout(() => {
        try {
          const twitchDiv = document.getElementById("twitch-embed-live");
          if (!twitchDiv) {
            console.error('Could not find element with id "twitch-embed-live"');
            return;
          }

          // Clear any existing content
          twitchDiv.innerHTML = "";

          // Initialize the Twitch embed
          if (window.Twitch && window.Twitch.Embed) {
            new window.Twitch.Embed("twitch-embed-live", {
              width: "100%",
              height: 400,
              channel: channelName,
              layout: "video",
              autoplay: true,
              muted: false,
              parent: [window.location.hostname],
            });

            setIsTwitchLoaded(true);
          } else {
            console.error("Twitch embed API not available");
          }
        } catch (error) {
          console.error("Error initializing Twitch embed:", error);
        }
      }, 100); // Small delay to ensure DOM is ready
    };

    script.onerror = (e) => {
      console.error("Error loading Twitch script:", e);
    };

    document.body.appendChild(script);

    // Clean up on unmount
    return () => {
      mountedRef = false;
      setIsTwitchLoaded(false);

      // Remove all Twitch scripts
      document
        .querySelectorAll('script[src="https://embed.twitch.tv/embed/v1.js"]')
        .forEach((el) => el.remove());

      // Clear the container
      const embedElement = document.getElementById("twitch-embed-live");
      if (embedElement) {
        embedElement.innerHTML = "";
      }
    };
  }, [match?.stream_url, showStream]);

  // Helper functions (must be defined before useMemo hooks that use them)

  // Calculate rating (simplified HLTV 2.0)
  const calculateRating = (player: any): number => {
    if (player.rounds_played === 0) return 0;

    const adr = player.damage / player.rounds_played;
    const kpr = player.kills / player.rounds_played;
    const kastPercent = player.kast / 100;

    return ((kpr * 0.679) + (kastPercent * 0.7) + (adr / 82.5)) / 2.7;
  };

  // Format round reason to match RoundsVisualTimeline expectations
  const formatRoundReason = (reason: number): string => {
    const reasons: { [key: number]: string } = {
      1: "Bomb Exploded",
      7: "Bomb Defused",
      8: "CT Win",
      9: "T Win",
      12: "Round Draw" // Target saved/time ran out - shows stopwatch icon
    };
    return reasons[reason] || "CT Win";
  };

  // Determine which data source to use: historical for finished maps, live SSE for live/created maps
  const activeData = useMemo(() => {
    const selectedMap = mapStatus?.maps.find(m => m.map_number === selectedMapNumber);
    const isViewingFinishedMap = selectedMap?.status === 'finished' && historicalData;

    if (isViewingFinishedMap) {
      console.log('[LiveMatchView] Using historical data for finished map', selectedMapNumber);
      return {
        liveState: historicalData.liveState,
        roundHistory: historicalData.roundHistory,
        playerStats: historicalData.playerStats,
        isHistorical: true
      };
    } else {
      // Filter SSE data by get5_match_id for the selected map
      const get5MatchId = selectedMap?.get5_match_id ?? null;

      const filteredRoundHistory = get5MatchId !== null
        ? sseRoundHistory.filter((r: any) => r.get5_match_id === get5MatchId)
        : sseRoundHistory;

      const filteredPlayerStats = get5MatchId !== null
        ? ssePlayerStats.filter((p: any) => p.get5_match_id === get5MatchId)
        : ssePlayerStats;

      // Check if SSE live state matches the selected map
      const liveStateMatchesSelectedMap = !selectedMap ||
        !sseLiveState ||
        sseLiveState.get5_match_id === selectedMap.get5_match_id;

      // If viewing a specific map that doesn't match SSE state, create a synthetic state
      const effectiveLiveState = liveStateMatchesSelectedMap ? sseLiveState : {
        ...sseLiveState,
        map_name: selectedMap?.map_name || sseLiveState?.map_name,
        get5_match_id: selectedMap?.get5_match_id || sseLiveState?.get5_match_id
      };

      console.log('[LiveMatchView] Using live SSE data', {
        selectedMapNumber,
        selectedMap: selectedMap?.map_name,
        get5MatchId,
        sseGet5MatchId: sseLiveState?.get5_match_id,
        liveStateMatchesSelectedMap,
        totalRounds: sseRoundHistory.length,
        filteredRounds: filteredRoundHistory.length,
        totalPlayers: ssePlayerStats.length,
        filteredPlayers: filteredPlayerStats.length
      });

      return {
        liveState: effectiveLiveState,
        roundHistory: filteredRoundHistory,
        playerStats: filteredPlayerStats,
        isHistorical: false
      };
    }
  }, [sseLiveState, sseRoundHistory, ssePlayerStats, historicalData, selectedMapNumber, mapStatus]);

  // Extract active data for easier access
  const { liveState, roundHistory, playerStats } = activeData;

  // Notify parent component when map state changes
  useEffect(() => {
    if (!onMapStateChange || !liveState) return;

    const selectedMap = mapStatus?.maps.find(m => m.map_number === selectedMapNumber);
    const mapName = selectedMap?.map_name || liveState.map_name || '';
    const isLiveMap = selectedMap?.status === 'live';

    // Find which map is actually live (for BO3)
    const liveMap = mapStatus?.maps.find(m => m.status === 'live');
    const liveMapNumber = liveMap?.map_number;

    // Calculate series score from map status if available
    let team1SeriesScore = liveState.team1_series_score || 0;
    let team2SeriesScore = liveState.team2_series_score || 0;

    // If we have map status, we can calculate the actual series score
    if (mapStatus?.maps) {
      // This would require knowing which team won each finished map
      // For now, use the live state values
      team1SeriesScore = liveState.team1_series_score || 0;
      team2SeriesScore = liveState.team2_series_score || 0;
    }

    onMapStateChange({
      mapName: mapName,
      mapNumber: selectedMapNumber || undefined,
      team1SeriesScore: team1SeriesScore,
      team2SeriesScore: team2SeriesScore,
      team1Score: liveState.team1_score || 0,
      team2Score: liveState.team2_score || 0,
      isLiveMap: isLiveMap,
      liveMapNumber: liveMapNumber,
      currentRound: isLiveMap ? liveState.round_number : undefined,
      maxRounds: 24
    });
  }, [onMapStateChange, selectedMapNumber, liveState, mapStatus]);

  // Match webhook team names to actual match teams (case-insensitive)
  const { matchedTeam1, matchedTeam2 } = useMemo(() => {
    if (!liveState) {
      return { matchedTeam1: null, matchedTeam2: null };
    }

    // If no match object provided, use webhook data directly
    if (!match) {
      console.warn('[LiveMatchView] No match object provided - falling back to webhook team names:', {
        team1: liveState.team1_name,
        team2: liveState.team2_name,
        team1Id: liveState.team1_id,
        team2Id: liveState.team2_id
      });
      return {
        matchedTeam1: {
          dbTeam: null,
          webhookId: liveState.team1_id,
          webhookName: liveState.team1_name,
          score: liveState.team1_score,
          side: liveState.team1_side,
          seriesScore: liveState.team1_series_score
        },
        matchedTeam2: {
          dbTeam: null,
          webhookId: liveState.team2_id,
          webhookName: liveState.team2_name,
          score: liveState.team2_score,
          side: liveState.team2_side,
          seriesScore: liveState.team2_series_score
        }
      };
    }

    // Normalize team names for comparison (Get5 removes parentheses)
    const normalizeTeamName = (name: string) => {
      return name
        .toLowerCase()
        .replace(/[().]/g, '')  // Remove parentheses and periods
        .replace(/\s+/g, ' ')  // Normalize whitespace
        .trim();
    };

    const webhookTeam1Name = normalizeTeamName(liveState.team1_name || '');
    const webhookTeam2Name = normalizeTeamName(liveState.team2_name || '');
    const dbTeam1Name = normalizeTeamName(match.team1.teamName || '');
    const dbTeam2Name = normalizeTeamName(match.team2.teamName || '');

    // Check if webhook team1 matches database team1
    const team1MatchesTeam1 = webhookTeam1Name === dbTeam1Name;
    const team1MatchesTeam2 = webhookTeam1Name === dbTeam2Name;

    console.log('[LiveMatchView] Team matching:', {
      webhookTeam1Name,
      webhookTeam2Name,
      dbTeam1Name,
      dbTeam2Name,
      team1MatchesTeam1,
      team1MatchesTeam2
    });

    // Return matched teams based on name comparison
    if (team1MatchesTeam1) {
      // Webhook order matches database order
      return {
        matchedTeam1: {
          dbTeam: match.team1,
          webhookId: liveState.team1_id,
          webhookName: liveState.team1_name,
          score: liveState.team1_score,
          side: liveState.team1_side,
          seriesScore: liveState.team1_series_score
        },
        matchedTeam2: {
          dbTeam: match.team2,
          webhookId: liveState.team2_id,
          webhookName: liveState.team2_name,
          score: liveState.team2_score,
          side: liveState.team2_side,
          seriesScore: liveState.team2_series_score
        }
      };
    } else {
      // Webhook order is reversed from database order
      return {
        matchedTeam1: {
          dbTeam: match.team1,
          webhookId: liveState.team2_id,
          webhookName: liveState.team2_name,
          score: liveState.team2_score,
          side: liveState.team2_side,
          seriesScore: liveState.team2_series_score
        },
        matchedTeam2: {
          dbTeam: match.team2,
          webhookId: liveState.team1_id,
          webhookName: liveState.team1_name,
          score: liveState.team1_score,
          side: liveState.team1_side,
          seriesScore: liveState.team1_series_score
        }
      };
    }
  }, [liveState, match]);

  // Extract team information using matched teams
  const team1Info: TeamInfo | null = useMemo(() => {
    if (!matchedTeam1) return null;

    // Use dbTeam if available, otherwise fallback to webhook name
    const teamName = matchedTeam1.dbTeam?.teamName || matchedTeam1.webhookName || 'Team 1';
    const teamLogo = matchedTeam1.dbTeam?.teamLogo || '/images/teams/default-logo.png';
    // Use database team ID for routing (can be UUID string or number), fallback to webhook ID
    const teamId = matchedTeam1.dbTeam?.teamId || matchedTeam1.webhookId || 0;
    // ALWAYS use webhook ID for filtering player stats (not database ID!)
    const webhookTeamId = matchedTeam1.webhookId || 0;

    if (!matchedTeam1.dbTeam) {
      console.warn('[LiveMatchView] Team1 using fallback data - no database team available:', {
        webhookName: matchedTeam1.webhookName,
        webhookId: matchedTeam1.webhookId,
        usingDefaultLogo: true
      });
    }

    console.log('[LiveMatchView] Team1 Info created:', {
      teamId: teamId,
      webhookTeamId: webhookTeamId,
      teamName: teamName
    });

    return {
      teamId,
      webhookTeamId,
      teamName,
      teamLogo,
      score: matchedTeam1.score || 0,
      side: matchedTeam1.side || "",
      seriesScore: matchedTeam1.seriesScore || 0
    };
  }, [matchedTeam1]);

  const team2Info: TeamInfo | null = useMemo(() => {
    if (!matchedTeam2) return null;

    // Use dbTeam if available, otherwise fallback to webhook name
    const teamName = matchedTeam2.dbTeam?.teamName || matchedTeam2.webhookName || 'Team 2';
    const teamLogo = matchedTeam2.dbTeam?.teamLogo || '/images/teams/default-logo.png';
    // Use database team ID for routing (can be UUID string or number), fallback to webhook ID
    const teamId = matchedTeam2.dbTeam?.teamId || matchedTeam2.webhookId || 0;
    // ALWAYS use webhook ID for filtering player stats (not database ID!)
    const webhookTeamId = matchedTeam2.webhookId || 0;

    if (!matchedTeam2.dbTeam) {
      console.warn('[LiveMatchView] Team2 using fallback data - no database team available:', {
        webhookName: matchedTeam2.webhookName,
        webhookId: matchedTeam2.webhookId,
        usingDefaultLogo: true
      });
    }

    console.log('[LiveMatchView] Team2 Info created:', {
      teamId: teamId,
      webhookTeamId: webhookTeamId,
      teamName: teamName
    });

    return {
      teamId,
      webhookTeamId,
      teamName,
      teamLogo,
      score: matchedTeam2.score || 0,
      side: matchedTeam2.side || "",
      seriesScore: matchedTeam2.seriesScore || 0
    };
  }, [matchedTeam2]);

  // Convert live player stats to simple format for display
  const team1Players: LivePlayerStats[] = useMemo(() => {
    if (!playerStats || !team1Info) {
      console.log('[LiveMatchView] Team1 players - Missing data:', {
        hasPlayerStats: !!playerStats,
        hasTeam1Info: !!team1Info
      });
      return [];
    }

    // CRITICAL: Filter by webhookTeamId (Get5 ID), NOT teamId (database ID)
    const filtered = playerStats.filter(p => p.team_id === team1Info.webhookTeamId);
    console.log('[LiveMatchView] Team1 players processing:', {
      webhookTeamId: team1Info.webhookTeamId,
      databaseTeamId: team1Info.teamId,
      teamName: team1Info.teamName,
      totalPlayerStats: playerStats.length,
      filteredPlayers: filtered.length,
      allPlayerTeamIds: playerStats.map(p => p.team_id),
      players: filtered.map(p => ({
        steamId: p.steam_id,
        name: p.player_name,
        kills: p.kills,
        deaths: p.deaths,
        teamId: p.team_id
      }))
    });

    const mapped = filtered
      .map(player => ({
        steam_id: player.steam_id,
        player_name: player.player_name || 'Unknown',
        kills: player.kills,
        deaths: player.deaths,
        assists: player.assists,
        adr: player.rounds_played > 0 ? Math.round(player.damage / player.rounds_played) : 0,
        kast: player.kast,
        headshot_kills: player.headshot_kills,
        mvp: player.mvp,
        rating: calculateRating(player)
      }))
      .sort((a, b) => b.rating - a.rating);

    console.log('[LiveMatchView] Team1 final player list:', {
      count: mapped.length,
      players: mapped.map(p => `${p.player_name} (${p.kills}-${p.deaths})`)
    });

    return mapped;
  }, [playerStats, team1Info]);

  const team2Players: LivePlayerStats[] = useMemo(() => {
    if (!playerStats || !team2Info) {
      console.log('[LiveMatchView] Team2 players - Missing data:', {
        hasPlayerStats: !!playerStats,
        hasTeam2Info: !!team2Info
      });
      return [];
    }

    // CRITICAL: Filter by webhookTeamId (Get5 ID), NOT teamId (database ID)
    const filtered = playerStats.filter(p => p.team_id === team2Info.webhookTeamId);
    console.log('[LiveMatchView] Team2 players processing:', {
      webhookTeamId: team2Info.webhookTeamId,
      databaseTeamId: team2Info.teamId,
      teamName: team2Info.teamName,
      totalPlayerStats: playerStats.length,
      filteredPlayers: filtered.length,
      allPlayerTeamIds: playerStats.map(p => p.team_id),
      players: filtered.map(p => ({
        steamId: p.steam_id,
        name: p.player_name,
        kills: p.kills,
        deaths: p.deaths,
        teamId: p.team_id
      }))
    });

    const mapped = filtered
      .map(player => ({
        steam_id: player.steam_id,
        player_name: player.player_name || 'Unknown',
        kills: player.kills,
        deaths: player.deaths,
        assists: player.assists,
        adr: player.rounds_played > 0 ? Math.round(player.damage / player.rounds_played) : 0,
        kast: player.kast,
        headshot_kills: player.headshot_kills,
        mvp: player.mvp,
        rating: calculateRating(player)
      }))
      .sort((a, b) => b.rating - a.rating);

    console.log('[LiveMatchView] Team2 final player list:', {
      count: mapped.length,
      players: mapped.map(p => `${p.player_name} (${p.kills}-${p.deaths})`)
    });

    return mapped;
  }, [playerStats, team2Info]);

  // Convert round history to Round format for timeline
  const roundsTimeline: Round[] = useMemo(() => {
    if (!roundHistory || !team1Info || !team2Info) return [];

    return roundHistory.map(round => ({
      round: round.round_number,
      // CRITICAL: Compare with webhookTeamId (Get5 ID), not teamId (database ID)
      winner: round.winner_team_id === team1Info.webhookTeamId ? 'team1' : 'team2',
      reason: formatRoundReason(round.reason || 0)
    }));
  }, [roundHistory, team1Info, team2Info]);

  // Load mappool for the match's season
  useEffect(() => {
    const loadMappool = async () => {
      if (match && match.season) {
        try {
          const seasonMappool = await getMappool(match.season);
          setCurrentSeasonMaps(seasonMappool);
        } catch (error) {
          console.error('Error loading mappool:', error);
          setCurrentSeasonMaps([]);
        }
      }
    };
    loadMappool();
  }, [match, getMappool]);

  // Parse veto data if available
  const mapPool = currentSeasonMaps;
  const vetoSteps = match?.veto && match.team1 && match.team2
    ? parseVetoLog(match.veto, match.team1, match.team2)
    : [];

  if (isLoading && !liveState) {
    return (
      <div className="live-match-view">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading live match...</p>
        </div>
      </div>
    );
  }

  // If we don't have data yet, show empty scoreboards instead of error messages
  if (!liveState || !team1Info || !team2Info) {
    console.log('[LiveMatchView] Showing empty scoreboards while waiting for data:', {
      hasLiveState: !!liveState,
      hasTeam1Info: !!team1Info,
      hasTeam2Info: !!team2Info
    });

    // Get fallback team info from match prop if available
    const fallbackTeam1 = match ? {
      teamName: match.team1.teamName,
      teamLogo: `https://api.ncseries.gg/images/teams/${match.team1.teamId}.png`,
      teamId: match.team1.teamId, // Use directly - can be UUID or number
      webhookTeamId: 0, // Unknown at this point
      score: 0,
      side: 'UNKNOWN',
      seriesScore: 0
    } : null;

    const fallbackTeam2 = match ? {
      teamName: match.team2.teamName,
      teamLogo: `https://api.ncseries.gg/images/teams/${match.team2.teamId}.png`,
      teamId: match.team2.teamId, // Use directly - can be UUID or number
      webhookTeamId: 0, // Unknown at this point
      score: 0,
      side: 'UNKNOWN',
      seriesScore: 0
    } : null;

    return (
      <>
        <div className="live-match-view">
          {/* Show empty scoreboards with team info */}
          {fallbackTeam1 && (
            <LiveTeamStatsSection
              teamName={fallbackTeam1.teamName}
              teamLogo={fallbackTeam1.teamLogo}
              teamId={fallbackTeam1.teamId}
              players={[]}
            />
          )}

          {/* Show message that match is starting */}
          <div className="rounds-timeline-container">
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: '#888',
              fontSize: '14px'
            }}>
              <div style={{ marginBottom: '0.5rem', fontSize: '16px', color: '#fff' }}>
                Waiting for match to start...
              </div>
              <div>The scoreboard will update automatically once the first round begins.</div>
            </div>
          </div>

          {fallbackTeam2 && (
            <LiveTeamStatsSection
              teamName={fallbackTeam2.teamName}
              teamLogo={fallbackTeam2.teamLogo}
              teamId={fallbackTeam2.teamId}
              players={[]}
            />
          )}

          {/* Live Update Indicator */}
          <div className="live-update-indicator">
            <span className="pulse-dot"></span>
            <span>Waiting for match data...</span>
          </div>

          {/* Live Stream Section - Show even while waiting */}
          {match?.stream_url && (
            <div className="match-stream-section" style={{ marginTop: '2rem' }}>
              {showStream ? (
                <>
                  {/* Stream header with minimize button */}
                  <div className="stream-header">
                    <h3>Live Stream</h3>
                    <button
                      className="stream-minimize-button"
                      onClick={() => setShowStream(false)}
                    >
                      <span>Minimize</span>
                    </button>
                  </div>

                  {/* Stream content */}
                  <div className="stream-content">
                    <div id="twitch-embed-live" className="twitch-container"></div>
                  </div>
                </>
              ) : (
                /* Show stream button when minimized */
                <button
                  className="stream-restore-button"
                  onClick={() => setShowStream(true)}
                >
                  <FaTwitch /> <span>Show Stream</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Veto Process - Show if veto data is available */}
        {vetoSteps.length > 0 && (
          <VetoSection vetoSteps={vetoSteps} mapPool={mapPool} />
        )}
      </>
    );
  }

  return (
    <>
      <div className="live-match-view">
        {/* Match Hero Header */}
        {showHero && match && team1Info && team2Info && liveState && (
          <MatchHero
            team1={{
              teamId: String(team1Info.teamId),
              teamName: team1Info.teamName,
              teamLogo: team1Info.teamLogo,
              season: match.season || 3,
            }}
            team2={{
              teamId: String(team2Info.teamId),
              teamName: team2Info.teamName,
              teamLogo: team2Info.teamLogo,
              season: match.season || 3,
            }}
            team1Score={liveState.team1_score || 0}
            team2Score={liveState.team2_score || 0}
            status="live"
            currentMap={liveState.map_name}
            currentRound={liveState.current_round}
            maxRounds={liveState.max_rounds || 24}
            seriesScore={
              liveState.team1_series_score !== undefined && liveState.team2_series_score !== undefined
                ? {
                    team1: liveState.team1_series_score,
                    team2: liveState.team2_series_score,
                  }
                : undefined
            }
            liveMapNumber={mapStatus?.maps.find(m => m.status === 'live')?.map_number}
            isViewingLiveMap={mapStatus?.maps.find(m => m.map_number === selectedMapNumber)?.status === 'live' || !selectedMapNumber}
            getTeamLogoUrl={getTeamLogoUrl}
            formatDateTime={formatDateTime}
          />
        )}

        {/* Post-Match Disclaimer */}
        {isPostMatch && !hasDetailedStats && (
          <div style={{
            backgroundColor: '#1a1a2e',
            border: '2px solid #e94560',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#e94560',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}>
              <span>⏳</span>
              <span>Match Ended - Processing Detailed Stats</span>
            </div>
            <div style={{
              fontSize: '14px',
              color: '#aaa',
              marginBottom: '0.5rem'
            }}>
              The stats shown below are preliminary from the live match.
            </div>
            <div style={{
              fontSize: '14px',
              color: '#fff'
            }}>
              Detailed statistics will be available shortly. The page will automatically update when ready.
            </div>
            <div style={{
              fontSize: '12px',
              color: '#888',
              marginTop: '0.5rem',
              fontStyle: 'italic'
            }}>
              Checking for detailed stats every 60 seconds...
            </div>
          </div>
        )}

        {/* Map Selector for BO3 */}
        {mapStatus && mapStatus.maps.length > 1 && (
          <div className="map-selector">
            <h3>Match Maps</h3>
            <div className="map-buttons">
              {mapStatus.maps.map((map) => {
                const isSelected = map.map_number === selectedMapNumber;
                const statusIcon = map.status === 'finished' ? <FaCheckCircle /> :
                                   map.status === 'live' ? <FaCircle className="live-pulse" /> :
                                   <FaClock />;

                return (
                  <button
                    key={map.map_number}
                    className={`map-button ${isSelected ? 'selected' : ''} ${map.status}`}
                    onClick={() => setSelectedMapNumber(map.map_number)}
                  >
                    <span className="map-status-icon">{statusIcon}</span>
                    <span className="map-name">{map.map_name.replace('de_', '')}</span>
                    <span className="map-label">Map {map.map_number}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Team 1 Statistics */}
        <LiveTeamStatsSection
          teamName={team1Info.teamName}
          teamLogo={team1Info.teamLogo}
          teamId={team1Info.teamId}
          players={team1Players}
          score={liveState?.team1_score}
          isWinner={liveState && liveState.team1_score > liveState.team2_score}
        />

        {/* Round History Timeline */}
        {roundsTimeline.length > 0 && (
          <div className="rounds-timeline-container">
            <RoundsVisualTimeline
              roundsTimeline={roundsTimeline}
            />
          </div>
        )}

        {/* Team 2 Statistics */}
        <LiveTeamStatsSection
          teamName={team2Info.teamName}
          teamLogo={team2Info.teamLogo}
          teamId={team2Info.teamId}
          players={team2Players}
          score={liveState?.team2_score}
          isWinner={liveState && liveState.team2_score > liveState.team1_score}
        />

        {/* Live Update Indicator */}
        {isLive && (
          <div className="live-update-indicator">
            <span className="pulse-dot"></span>
            <span>Updates automatically</span>
          </div>
        )}

        {/* Live Stream Section - Show if available */}
        {match?.stream_url && (
          <div className="match-stream-section" style={{ marginTop: '2rem' }}>
            {showStream ? (
              <>
                {/* Stream header with minimize button */}
                <div className="stream-header">
                  <h3>Live Stream</h3>
                  <button
                    className="stream-minimize-button"
                    onClick={() => setShowStream(false)}
                  >
                    <span>Minimize</span>
                  </button>
                </div>

                {/* Stream content */}
                <div className="stream-content">
                  <div id="twitch-embed-live" className="twitch-container"></div>
                </div>
              </>
            ) : (
              /* Show stream button when minimized */
              <button
                className="stream-restore-button"
                onClick={() => setShowStream(true)}
              >
                <FaTwitch /> <span>Show Stream</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Veto Process - Show if veto data is available */}
      {vetoSteps.length > 0 && (
        <VetoSection vetoSteps={vetoSteps} mapPool={mapPool} />
      )}
    </>
  );
};

export default LiveMatchView;
